import React, { useEffect, useRef, useState, cloneElement } from "react";
import {
  Box, Collapse, Drawer, IconButton, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, Typography, Tooltip,
} from "@mui/material";
import {
  ChevronLeft, ExpandLess, ExpandMore, Star, StarBorder,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { user } from "localStorage";
import { hasPageAccess, ALL_PAGES, ALWAYS_VISIBLE_PAGES, NAV_PAGES, PAGE_GROUPS } from "utils/pagePermissions";
import { useUserRefreshTick } from "hooks/useUserRefreshTick";
import { useFavouritesContext } from "hooks/FavouritesContext";
import { capitalizeFirstLetter } from "utils";
import brandLogo from "assets/icon-white-transparent.png";

// Slugs that stay in the page registry (so the role editor + mobile-staff keep using
// them) but are hidden from the WEB sidebar: their web flows were folded into other
// pages. Their routes redirect there (see App.js).
//   - wallet/report → retired on web (kept on mobile)
//   - packages/change-requests → now the Packages page's "Change Requests" tab
//     (same permission); the old route redirects there
// Top-up/Adjust/Purchase Order/Refund no longer need an entry here — they're
// tabs under wallet/accounts now (shared/pageSlugs.json), so ALL_PAGES/NAV_GROUPS
// never surfaces them as top-level sidebar items in the first place.
const WEB_SIDEBAR_HIDDEN = new Set([
  "wallet/report",
  "packages/change-requests",
]);

const NAV_GROUPS = PAGE_GROUPS.map((group) => ({
  group,
  items: ALL_PAGES.filter((p) => p.group === group && !WEB_SIDEBAR_HIDDEN.has(p.slug)),
}));

const FAVOURITES_GROUP = "⭐ Favourites";
const COLLAPSED_WIDTH = "72px";

const initials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase();
};

const Sidebar = ({ user: currentUser, drawerWidth, isSidebarOpen, setIsSidebarOpen, isNonMobile }) => {
  const { pathname } = useLocation();
  const [active, setActive] = useState("");
  const navigate = useNavigate();
  useUserRefreshTick(); // re-render when the cached profile refreshes — see hooks/useUserRefreshTick.js
  const _user = user();
  const { favourites, isFavourite, toggleFavourite } = useFavouritesContext();

  // Permission-gated pages first, then the always-visible ones (Shipping Calculator)
  // appended to their group — they carry no permission, so everyone sees them.
  const visibleGroups = NAV_GROUPS.map((g) => ({
    ...g,
    items: [
      ...g.items.filter((item) => hasPageAccess(_user, item.slug)),
      ...ALWAYS_VISIBLE_PAGES.filter((item) => item.group === g.group),
    ],
  })).filter((g) => g.items.length > 0);

  const totalItems = visibleGroups.reduce((sum, g) => sum + g.items.length, 0);

  const [openGroups, setOpenGroups] = useState(() => ({
    [FAVOURITES_GROUP]: true,
    ...Object.fromEntries(
      visibleGroups.map((g) => [g.group, totalItems < 15 || g.group === "General Management"])
    ),
  }));

  // Pages starred by this user that they still have access to
  const favouritePages = favourites
    .map((slug) => NAV_PAGES.find((p) => p.slug === slug))
    .filter((p) => p && !WEB_SIDEBAR_HIDDEN.has(p.slug) && hasPageAccess(_user, p.slug));

  // Once favourites load from DB, collapse regular groups if there are any
  const favouritesLoaded = useRef(false);
  useEffect(() => {
    if (!favouritesLoaded.current && favouritePages.length > 0) {
      favouritesLoaded.current = true;
      setOpenGroups((prev) => ({
        ...prev,
        ...Object.fromEntries(visibleGroups.map((g) => [g.group, false])),
      }));
    }
  }, [favouritePages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setActive(pathname.substring(1)); }, [pathname]);

  const toggleGroup = (groupName) =>
    setOpenGroups((prev) => ({ ...prev, [groupName]: !prev[groupName] }));

  // Branch Cash jumps straight to the user's default station (skipping the
  // all-stations overview) — same as mobile-staff's Home tab shortcut. Falls
  // back to the overview if they have no default (or no stations at all).
  const navPathFor = (slug) => {
    if (slug === "branch-cash") {
      const stations = _user?.stations || [];
      const def = stations.find((s) => s.default) || stations[0];
      if (def) return `/branch-cash/${encodeURIComponent(def.id)}`;
    }
    return `/${slug}`;
  };

  const renderItem = (slug, label, icon, expanded) => {
    const isActive = active === slug;
    const starred = isFavourite(slug);
    const sizedIcon = icon ? cloneElement(icon, { sx: { fontSize: 18 } }) : null;

    const row = (
      <ListItemButton
        onClick={() => {
          navigate(navPathFor(slug));
          setActive(slug);
          if (!isNonMobile) setIsSidebarOpen(false);
        }}
        sx={{
          position: "relative",
          gap: "11px",
          py: "10px",
          px: expanded ? "10px" : "8px",
          mb: "2px",
          borderRadius: "10px",
          justifyContent: expanded ? "flex-start" : "center",
          backgroundColor: isActive ? "rgba(139,92,246,.18)" : "transparent",
          "&:hover": { backgroundColor: "rgba(255,255,255,.07)" },
        }}
      >
        {isActive && (
          <Box sx={{
            position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)",
            width: "3px", height: "22px", borderRadius: "0 3px 3px 0",
            background: "linear-gradient(180deg,#a78bfa,#6d28d9)",
          }} />
        )}
        <ListItemIcon sx={{
          minWidth: 0, width: 32, height: 32, flex: "none", borderRadius: "9px",
          display: "flex", alignItems: "center", justifyContent: "center",
          backgroundColor: isActive ? "rgba(109,40,217,.35)" : "rgba(255,255,255,.06)",
          color: isActive ? "#c4b5fd" : "#6d5e8a",
        }}>
          {sizedIcon}
        </ListItemIcon>
        {expanded && (
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontSize: "13px",
              fontWeight: isActive ? 700 : 500,
              color: isActive ? "#e9e3fb" : "#b9b4c0",
            }}
          />
        )}
        {expanded && (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); toggleFavourite(slug); }}
            sx={{
              color: starred ? "#f59e0b" : "#6d5e8a",
              opacity: starred ? 1 : 0.35,
              "&:hover": { opacity: 1 },
            }}
          >
            {starred ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
          </IconButton>
        )}
      </ListItemButton>
    );

    return (
      <ListItem key={slug} disablePadding sx={{ px: "12px" }}>
        {expanded ? row : (
          <Tooltip title={label} placement="right">
            <Box sx={{ width: "100%" }}>{row}</Box>
          </Tooltip>
        )}
      </ListItem>
    );
  };

  const renderGroupHeader = (label, isOpen, onClick, color = "#6d5e8a") => (
    <Box sx={{ px: "12px", mb: "4px" }}>
      <ListItemButton
        onClick={onClick}
        sx={{ borderRadius: "10px", px: "10px", py: "8px", gap: "10px", "&:hover": { backgroundColor: "rgba(255,255,255,.05)" } }}
      >
        <Typography sx={{ flex: 1, fontSize: "10.5px", fontWeight: 800, letterSpacing: ".12em", color, textTransform: "uppercase", textAlign: "left" }}>
          {label}
        </Typography>
        {isOpen ? <ExpandLess sx={{ color, fontSize: "1.1rem" }} /> : <ExpandMore sx={{ color, fontSize: "1.1rem" }} />}
      </ListItemButton>
    </Box>
  );

  const renderContent = (expanded) => (
    <Box sx={{
      width: "100%", height: "100%", display: "flex", flexDirection: "column",
      overflow: "hidden", background: "linear-gradient(180deg,#1b1728 0%,#14101f 100%)",
    }}>
      {/* brand */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: "11px", flex: "none", height: "64px",
        px: expanded ? "20px" : 0, justifyContent: expanded ? "flex-start" : "center",
        borderBottom: "1px solid #2a2244",
      }}>
        <Box sx={{
          width: 34, height: 34, flex: "none", borderRadius: "10px",
          background: "linear-gradient(135deg,#7c3aed,#4c1d95)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 12px rgba(109,40,217,.45)",
        }}>
          <Box
            component="img"
            src={brandLogo}
            alt="Winner Express"
            sx={{ width: 22, height: 22, objectFit: "contain", display: "block" }}
          />
        </Box>
        {expanded && (
          <Box>
            <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#fff", lineHeight: 1.1 }}>Winner Express</Typography>
            <Typography sx={{ fontSize: "10px", color: "#6d5e8a", fontWeight: 600, letterSpacing: ".06em", mt: "1px" }}>LOGISTICS PLATFORM</Typography>
          </Box>
        )}
      </Box>

      {/* nav scroll area */}
      <Box sx={{
        flex: 1, overflowY: "auto", py: "16px",
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": { background: "#3b2f5a", borderRadius: "4px" },
      }}>
        <List disablePadding>
          {favouritePages.length > 0 && (
            <Box>
              {expanded && renderGroupHeader(FAVOURITES_GROUP, openGroups[FAVOURITES_GROUP], () => toggleGroup(FAVOURITES_GROUP), "#f59e0b")}
              <Collapse in={openGroups[FAVOURITES_GROUP]} timeout="auto" unmountOnExit>
                <List disablePadding sx={{ mb: "10px" }}>
                  {favouritePages.map(({ slug, label, icon }) => renderItem(slug, label, icon, expanded))}
                </List>
              </Collapse>
            </Box>
          )}

          {visibleGroups.map(({ group, items }) => (
            <Box key={group}>
              {expanded && renderGroupHeader(group, openGroups[group], () => toggleGroup(group))}
              <Collapse in={openGroups[group]} timeout="auto" unmountOnExit>
                <List disablePadding sx={{ mb: "10px" }}>
                  {items.map(({ slug, label, icon }) => renderItem(slug, label, icon, expanded))}
                </List>
              </Collapse>
            </Box>
          ))}
        </List>
      </Box>

      {/* footer */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: "10px", flex: "none",
        px: expanded ? "16px" : 0, py: "14px", justifyContent: expanded ? "flex-start" : "center",
        borderTop: "1px solid #2a2244",
      }}>
        <Box sx={{
          width: 34, height: 34, flex: "none", borderRadius: "50%",
          background: "linear-gradient(135deg,#7c3aed,#4c1d95)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontWeight: 700, fontSize: "13px",
        }}>
          {initials(currentUser?.name)}
        </Box>
        {expanded && (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: "12.5px", fontWeight: 700, color: "#e9e3fb" }}>
                {currentUser?.name ? capitalizeFirstLetter(currentUser.name) : ""}
              </Typography>
              <Typography sx={{ fontSize: "10.5px", color: "#6d5e8a", fontWeight: 600 }}>
                {currentUser?.role
                  ? capitalizeFirstLetter(currentUser.role !== "agent" ? currentUser.role : `${currentUser.role} - ${currentUser.agentId}`)
                  : ""}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setIsSidebarOpen(false)}
              sx={{
                width: 28, height: 28, borderRadius: "8px", flex: "none",
                backgroundColor: "rgba(255,255,255,.06)", border: "1px solid #2a2244",
                "&:hover": { backgroundColor: "rgba(255,255,255,.12)" },
              }}
            >
              <ChevronLeft sx={{ fontSize: "16px", color: "#a094b8" }} />
            </IconButton>
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <Box component="nav" sx={{ flex: "none", height: "100%" }}>
      {isNonMobile ? (
        <Box sx={{
          width: isSidebarOpen ? drawerWidth : COLLAPSED_WIDTH,
          height: "100vh", flex: "none",
          transition: "width .25s cubic-bezier(.4,0,.2,1)",
          overflow: "hidden",
        }}>
          {renderContent(isSidebarOpen)}
        </Box>
      ) : (
        isSidebarOpen && (
          <Drawer
            open={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
            variant="temporary"
            anchor="left"
            transitionDuration={{ enter: 500, exit: 500 }}
            sx={{ "& .MuiDrawer-paper": { width: drawerWidth, boxSizing: "border-box", border: "none" } }}
          >
            {renderContent(true)}
          </Drawer>
        )
      )}
    </Box>
  );
};

export default Sidebar;
